import React, {
	useEffect,
	useState,
	useRef,
	useCallback,
	useMemo,
} from "react";

import { Link, useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setLoading, setModule } from "../../store/actions/commonAction";
import CommonDataService from "../../services/common";
import {
	AllowedUsergroup,
	GetIndexByValue,
	CDNWebsite,
	apiUrl,
	parseNumber,
} from "../../helpers/common";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Divider from "@mui/material/Divider";
import SaveIcon from "@mui/icons-material/Save";
import PhotoCamera from "@mui/icons-material/PhotoCamera";

import JoditEditor from "jodit-react";
import MessageDialog from "../MessageDialog";
import http from "../../http";

export default function AddRecentSales({ placeholder }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	let { id } = useParams();
	const editor = useRef(null);

	const company = useSelector((state) => state.commonReducer.user.company);
	const userGroup = useSelector((state) => state.commonReducer.user.usergroup);
	const token = useSelector((state) => state.commonReducer.user.token);
	const loading = useSelector((state) => state.commonReducer.loading);

	const [message, setMessage] = useState("");
	const [error, setError] = useState(false);

	const [thumbPath, setThumbPath] = useState([]);
	const [offices, setOffices] = useState([]);
	const [idOffice, setIdOffice] = useState(company);
	const [idOfficeAC, setIdOfficeAC] = useState(null);
	const [title, setTitle] = useState("");
	const [videoUrl, setVideoUrl] = useState("");
	const [active, setActive] = useState("");
	const [uniqueName, setUniqueName] = useState("");
	const [image, setImage] = useState("");
	const [content, setContent] = useState("");

	const LoadSelect = useCallback(() => {
		dispatch(setLoading(true));
		dispatch(setModule("Recent Sales"));

		if (typeof id !== "undefined") {
			CommonDataService.getRecentSalesById(id).then((res) => {
				setIdOffice(res.data.Row.id_office);
				setTitle(res.data.Row.title);
				setVideoUrl(res.data.Row.video_url);
				setActive(res.data.Row.active);
				setUniqueName(res.data.Row.unique_name);
				setContent(res.data.Row.content);
				setThumbPath(CDNWebsite() + res.data.Row.pic_thumb);

				dispatch(setLoading(false));
			});
		} else {
			dispatch(setLoading(false));
		}

		if (AllowedUsergroup(userGroup, "superuser-website")) {
			CommonDataService.getOffices().then((res) => {
				setOffices(res.data);
			});
		}
	}, [dispatch, id, userGroup]);

	useEffect(() => {
		setIdOfficeAC(GetIndexByValue(offices, idOffice));
	}, [offices, idOffice]);

	useEffect(() => {
		LoadSelect();
	}, [LoadSelect]);

	const handleSubmit = (event) => {
		event.preventDefault();

		const data = new FormData(event.currentTarget);

		if (typeof id === "undefined") {
			id = 0;
		}

		data.append("image", image);
		data.append("Id", id);
		data.append("IdOffice", parseNumber(idOffice));
		data.append("Title", title);
		data.append("VideoUrl", videoUrl);
		data.append("Content", content);
		data.append("Active", active);
		data.append("UniqueName", uniqueName);

		dispatch(setLoading(true));

		http
			.post("/recent_sales/save", data, {
				headers: {
					"Content-Type": "multipart/form-data",
					Authorization: "Bearer " + token,
				},
			})
			.then((res) => {
				dispatch(setLoading(false));
				navigate("/recent_sales" + window.location.search);
			})
			.catch(function (error) {
				setMessage(error.response.data.message);
				setError(true);
				dispatch(setLoading(false));
			});
	};

	const config = useMemo(() => {
		return {
			readonly: false,
			uploader: {
				url: apiUrl() + "/news/upload",
				headers: {
					Authorization: "Bearer " + token,
				},
			},
			imageDefaultWidth: "100%",
			placeholder: placeholder || "Start typings...",
		};
	}, [placeholder, token]);

	return (
		<Grid container spacing={3}>
			<Grid item xs={12}>
				<MessageDialog show={error} message={message} dialogClose={setError} />

				<Paper
					sx={{
						p: 2,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box component="form" onSubmit={(e) => handleSubmit(e)}>
						<Typography variant="h5">RecentSales Form</Typography>

						<Grid container spacing={3}>
							{AllowedUsergroup(userGroup, "superuser-website") && (
								<Grid item xs={12} sm={12}>
									<Autocomplete
										id="id_office"
										name="id_office"
										options={offices}
										getOptionLabel={(option) => option.name}
										isOptionEqualToValue={(option, value) =>
											option.value === value.value
										}
										fullWidth={true}
										onChange={(e, val) => {
											if (val !== null) {
												setIdOffice(val.id);
											} else {
												setIdOffice("");
											}
										}}
										value={idOfficeAC}
										sx={{ minWidth: 100 }}
										renderInput={(params) => (
											<TextField
												variant="standard"
												{...params}
												label="Office"
											/>
										)}
									/>
								</Grid>
							)}

							<Grid item xs={12} sm={12}>
								<div>
									<img alt="" height="100" src={thumbPath} />
								</div>
								<Button
									startIcon={<PhotoCamera />}
									variant="outlined"
									component="label"
								>
									Select Image
									<input
										hidden
										onChange={(e) => {
											setImage(e.target.files[0]);
											const file = e.target.files[0];
											let reader = new FileReader();
											reader.readAsDataURL(file);
											reader.onload = () => {
												setThumbPath(reader.result);
											};
										}}
										accept="image/*"
										multiple
										type="file"
									/>
								</Button>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									required
									label="Title"
									id="title"
									name="title"
									value={title}
									fullWidth
									variant="standard"
									onChange={(e) => setTitle(e.target.value)}
								/>
							</Grid>

							{typeof id !== "undefined" && (
								<Grid item xs={12} sm={6}>
									<TextField
										required
										label="Unique Name"
										id="uniqueName"
										name="uniqueName"
										value={uniqueName}
										fullWidth
										variant="standard"
										onChange={(e) => setUniqueName(e.target.value)}
									/>
								</Grid>
							)}
							<Grid item xs={12} sm={6}>
								<TextField
									label="Youtube ID"
									id="videoUrl"
									name="videoUrl"
									value={videoUrl}
									fullWidth
									variant="standard"
									onChange={(e) => setVideoUrl(e.target.value)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControlLabel
									control={
										<Checkbox
											checked={active === "true" || active === true}
											onChange={(e) => setActive(e.target.checked)}
										/>
									}
									label="Active"
								/>
							</Grid>

							<Grid item xs={12} sm={12}>
								<JoditEditor
									ref={editor}
									value={content}
									config={config}
									tabIndex={1} // tabIndex of textarea
									onBlur={(newContent) => setContent(newContent)} // preferred to use only this option to update the content for performance reasons
									onChange={(newContent) => {}}
								/>
							</Grid>

							<Grid item xs={12} sm={12}>
								<Divider />
							</Grid>

							<Grid item xs={12} sm={12}>
								<Button
									variant="contained"
									component={Link}
									to={"/recent_sales" + window.location.search}
									startIcon={<ArrowBackIcon />}
									sx={{ mr: 1 }}
								>
									Cancel
								</Button>
								<Button
									type="submit"
									variant="contained"
									disabled={loading}
									startIcon={<SaveIcon />}
								>
									{loading && (
										<span>
											<span className="spinner-border spinner-border-sm"></span>
											&nbsp;
										</span>
									)}
									<span> {loading ? "Please Wait" : "Save"}</span>
								</Button>
							</Grid>
						</Grid>
					</Box>
				</Paper>
			</Grid>
		</Grid>
	);
}
